// "Effective access" for one person (GET rbac/users/:userId/effective-access):
// their roles with where/until, every permission they end up with grouped by
// area, WHICH companies each payroll/compensation permission reaches, and the
// warnings an admin should read before saving anything (salaries everywhere,
// approve AND release, access ending soon).
//
// Four states, kept distinct: loading, error (never rendered as "no access"),
// genuinely empty (no roles, no permissions), and data.
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertOctagon, AlertTriangle, Info, ShieldCheck } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/features/shared/components/ui/sheet';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { PageBoundary } from '@/features/shared/components/PageBoundary';
import { getApiErrorMessage } from '@/lib/apiError';
import { formatDate } from '@/lib/orgFormat';
import {
  getEffectiveAccess,
  type AccessWarning,
  type AccessWarningSeverity,
  type EffectiveAccess,
  type PermissionReachRow,
} from '@/services/rbacService';

interface Props {
  userId: string | null;
  personName: string;
  /** Company name for a legal entity id (falls back to a short id). */
  entityName: (id: string) => string;
  onClose: () => void;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; variant: 'error' | 'denied'; message: string }
  | { kind: 'data'; data: EffectiveAccess };

const SEVERITY: Record<
  AccessWarningSeverity,
  { box: string; icon: React.ComponentType<{ className?: string }>; label: string }
> = {
  critical: { box: 'bg-danger-soft text-danger-strong', icon: AlertOctagon, label: 'Critical' },
  warning: { box: 'bg-warning-soft text-warning-strong', icon: AlertTriangle, label: 'Warning' },
  info: { box: 'bg-info-soft text-info-strong', icon: Info, label: 'Note' },
};

const SCOPE_LABEL: Record<string, string> = {
  ALL_AUTHORIZED_ENTITIES: 'All companies, including ones added later',
  SELECTED_ENTITIES: 'Selected companies',
};

/** Human wording for one warning — company names instead of ids where we have them. */
export function warningText(w: AccessWarning, entityName: (id: string) => string): string {
  if ((w.code === 'sod_approve_release' || w.code === 'sod_prepare_approve') && !w.allEntities && w.entityIds?.length) {
    const verb = w.code === 'sod_approve_release' ? 'approve AND release' : 'prepare AND approve';
    return `Can ${verb} payroll for ${w.entityIds.map(entityName).join(', ')} — separation of duties.`;
  }
  if (w.code === 'no_entities' && w.surfaces?.length) {
    // F4: what they cannot SEE, not which keys lack a company.
    return `Cannot see ${w.surfaces.join('; ')} — no company is granted for them. Give them a company to fix this.`;
  }
  if (w.code === 'assignment_expires' && w.date && (w.roleLabel || w.roleKey)) {
    return `${w.roleLabel || w.roleKey} access ends on ${formatDate(w.date)}.`;
  }
  return w.message;
}

const ReachCell: React.FC<{ row: PermissionReachRow; entityName: (id: string) => string }> = ({ row, entityName }) => {
  let main: React.ReactNode;
  switch (row.mode) {
    case 'all':
      main = <Badge variant="warning">All companies</Badge>;
      break;
    case 'unrestricted':
      main = (
        <Badge variant="warning" title="Not limited by company — your organization does not use company-level access yet.">
          All companies (no company limit)
        </Badge>
      );
      break;
    case 'no_entities':
      main = (
        <Badge variant="danger" title="Holds the permission, but no company is granted for it.">
          No company granted
        </Badge>
      );
      break;
    case 'selected':
      main = (
        <span className="flex flex-wrap gap-1">
          {row.entityIds.map((id) => (
            <Badge key={id} variant="neutral">
              {entityName(id)}
            </Badge>
          ))}
        </span>
      );
      break;
    default:
      main = <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="space-y-1">
      {main}
      {row.delegated && (
        <span className="block text-xs text-muted-foreground">
          Includes{' '}
          {row.delegated.allEntities
            ? 'all companies'
            : row.delegated.entityIds.map(entityName).join(', ')}{' '}
          via delegation
        </span>
      )}
    </div>
  );
};

export const EffectiveAccessContent: React.FC<{
  state: LoadState;
  entityName: (id: string) => string;
  onRetry: () => void;
}> = ({ state, entityName, onRetry }) => {
  if (state.kind === 'loading') {
    return <DataState variant="loading" title="Working out this person’s access…" />;
  }
  if (state.kind === 'error') {
    return (
      <DataState
        variant={state.variant}
        title={state.variant === 'denied' ? 'You can’t view this person’s access' : 'Couldn’t load effective access'}
        message={`${state.message} This does not mean they have no access.`}
        onRetry={onRetry}
      />
    );
  }
  const d = state.data;
  const noRoles = d.roles.length === 0;
  const noPerms = !d.fullAccess && d.permissionGroups.length === 0;
  if (noRoles && noPerms) {
    return (
      <DataState
        variant="empty"
        title="No roles and no permissions"
        message="This person has no role assigned, so they can only use their own self-service features."
      />
    );
  }

  const held = d.sensitiveReach.filter((r) => r.mode !== 'none');
  const notHeld = d.sensitiveReach.length - held.length;

  return (
    <div className="space-y-6" data-testid="effective-access">
      {/* Warnings */}
      <section aria-labelledby="ea-warnings">
        <h3 id="ea-warnings" className="text-sm font-semibold text-foreground mb-2">
          Warnings
        </h3>
        {d.warnings.length === 0 ? (
          <p className="text-sm text-muted-foreground">No warnings for this person’s access.</p>
        ) : (
          <ul className="space-y-2">
            {d.warnings.map((w, i) => {
              const s = SEVERITY[w.severity] ?? SEVERITY.info;
              const Icon = s.icon;
              return (
                <li
                  key={`${w.code}-${i}`}
                  className={`flex items-start gap-2 rounded-[10px] px-3 py-2 text-sm ${s.box}`}
                  data-severity={w.severity}
                >
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                  <span>
                    <span className="sr-only">{s.label}: </span>
                    {warningText(w, entityName)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Roles */}
      <section aria-labelledby="ea-roles">
        <h3 id="ea-roles" className="text-sm font-semibold text-foreground mb-2">
          Roles
        </h3>
        {noRoles ? (
          <p className="text-sm text-muted-foreground">No roles.</p>
        ) : (
          <ul className="divide-y divide-border">
            {d.roles.map((r, i) => (
              <li key={`${r.source}-${r.roleKey}-${i}`} className="py-2 flex flex-wrap items-center gap-2">
                <span className={`text-sm font-medium ${r.active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                  {r.roleLabel || r.roleKey}
                </span>
                {r.source === 'base' && <Badge variant="outline">Account role</Badge>}
                {r.entityScope === 'ALL_AUTHORIZED_ENTITIES' && (
                  <Badge variant="warning">{SCOPE_LABEL[r.entityScope]}</Badge>
                )}
                {r.entityScope === 'SELECTED_ENTITIES' &&
                  r.legalEntityIds.map((id) => (
                    <Badge key={id} variant="neutral">
                      {entityName(id)}
                    </Badge>
                  ))}
                {r.expiresAt && r.active && <Badge variant="info">Ends {formatDate(r.expiresAt)}</Badge>}
                {!r.active && <Badge variant="neutral">{r.status === 'revoked' ? 'Revoked' : 'Expired'}</Badge>}
                {r.supersededByScopedAssignment && (
                  <span className="text-xs text-muted-foreground">limited by its assignment</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Payroll & compensation reach */}
      <section aria-labelledby="ea-reach">
        <h3 id="ea-reach" className="text-sm font-semibold text-foreground mb-1">
          Payroll, compensation &amp; accounting — by company
        </h3>
        <p className="text-xs text-muted-foreground mb-2">
          Each permission counts only in the companies of the role that grants it.
        </p>
        {held.length === 0 ? (
          <p className="text-sm text-muted-foreground">Holds no payroll or compensation permission.</p>
        ) : (
          <div className="overflow-x-auto rounded-[10px] border border-border">
            <table className="w-full text-left text-sm" aria-label="Payroll and compensation reach">
              <thead className="bg-cream text-muted-foreground text-xs">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Permission
                  </th>
                  <th scope="col" className="px-3 py-2 font-medium">
                    Companies
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {held.map((r) => (
                  <tr key={r.permission}>
                    <td className="px-3 py-2 align-top">
                      <code className="text-xs text-foreground">{r.permission}</code>
                      {r.description && <span className="block text-xs text-muted-foreground">{r.description}</span>}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <ReachCell row={r} entityName={entityName} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {notHeld > 0 && held.length > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            {notHeld} other payroll/compensation permission{notHeld === 1 ? '' : 's'} not held.
          </p>
        )}
      </section>

      {/* All permissions */}
      <section aria-labelledby="ea-perms">
        <h3 id="ea-perms" className="text-sm font-semibold text-foreground mb-2">
          All permissions{d.permissionCount != null ? ` (${d.permissionCount})` : ''}
        </h3>
        {d.fullAccess ? (
          <p className="text-sm text-foreground">Every permission (full access).</p>
        ) : d.permissionGroups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No permissions.</p>
        ) : (
          <div className="space-y-3">
            {d.permissionGroups.map((g) => (
              <div key={g.group}>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.group}</p>
                <ul className="mt-1 flex flex-wrap gap-1">
                  {g.permissions.map((p) => (
                    <li key={p.key}>
                      <Badge variant={p.sensitive ? 'warning' : 'outline'} title={p.description ?? undefined}>
                        {p.key}
                      </Badge>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export const EffectiveAccessSheet: React.FC<Props> = ({ userId, personName, entityName, onClose }) => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async (id: string) => {
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'data', data: await getEffectiveAccess(id) });
    } catch (e) {
      const message = getApiErrorMessage(e, 'Could not load this person’s effective access');
      toast.error(getApiErrorMessage(e, 'Could not load this person’s effective access'));
      setState({ kind: 'error', variant: errorVariant(e), message });
    }
  }, []);

  useEffect(() => {
    if (userId) void load(userId);
  }, [userId, load]);

  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" aria-hidden /> Effective access
          </SheetTitle>
          <SheetDescription>
            What {personName} can do right now, and in which companies.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4">
          <PageBoundary compact title="Effective access">
            <EffectiveAccessContent
              state={state}
              entityName={entityName}
              onRetry={() => userId && void load(userId)}
            />
          </PageBoundary>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default EffectiveAccessSheet;
